import { apiClient } from './client'
import { API_ENDPOINTS } from '@/utils/constants'
import type { PaginatedResponse } from '@/types'

export type HelpRequestSource = 'cabinet' | 'landing' | 'mobile'
/** Локальный submitted + зеркало Plane Intake после sync */
export type HelpRequestStatus =
  | 'submitted'
  | 'pending'
  | 'accepted'
  | 'declined'
  | 'snoozed'
  | 'duplicate'

export type HelpRequestMessageSource = 'plane_comment' | 'system'

export interface HelpRequestMessage {
  id: number
  help_request_id: number
  body: string
  author_name: string | null
  source: HelpRequestMessageSource
  plane_created_at: string | null
  created_at: string | null
}

/**
 * Метаданные вложения. Ссылки на файл нет и не будет: сам файл лежит в задаче
 * Plane у поддержки, кабинет показывает только сколько файлов приложено.
 */
export interface HelpRequestAttachment {
  id: number
  help_request_id: number
  original_name: string
  mime_type: string
  size_bytes: number
  created_at: string | null
}

export interface HelpRequest {
  id: number
  user_id: number | null
  guest_name?: string | null
  guest_email?: string | null
  title: string
  body: string
  /** Может отсутствовать в Resource для клиента (внутренний id Plane) */
  plane_issue_id?: string | null
  source: HelpRequestSource
  status: HelpRequestStatus
  /** Вложения — в show; в index могут отсутствовать или быть урезанными */
  attachments?: HelpRequestAttachment[]
  /** Сколько файлов приложено — приходит всегда, даже когда attachments не загружены */
  attachments_count?: number
  /** Лента ответов — обычно только в GET /help/{id} */
  messages?: HelpRequestMessage[]
  created_at: string | null
  updated_at: string | null
}

export interface HelpRequestCreatePayload {
  title: string
  body: string
  /** Опционально при multipart: attachments[] */
  attachments?: File[]
}

export interface HelpListParams {
  page?: number
  per_page?: number
}

export interface HelpUploadProgress {
  loaded: number
  total: number
  percent: number
}

interface HelpRequestApiResponse {
  success?: boolean
  message?: string
  data?: HelpRequest
}

interface HelpRequestListApiResponse {
  success?: boolean
  message?: string
  data?: HelpRequest[]
  meta?: {
    current_page?: number
    last_page?: number
    per_page?: number
    total?: number
  }
}

interface HelpAttachmentApiResponse {
  success?: boolean
  message?: string
  data?: HelpRequestAttachment
}

/**
 * Обращения в поддержку: создание, список, карточка и вложения. /help
 */
export const helpApi = {
  create: async (
    payload: HelpRequestCreatePayload,
    onUploadProgress?: (progress: HelpUploadProgress) => void,
  ): Promise<HelpRequest> => {
    const hasFiles = (payload.attachments?.length ?? 0) > 0

    if (hasFiles) {
      const formData = new FormData()
      formData.append('title', payload.title)
      formData.append('body', payload.body)
      for (const file of payload.attachments ?? []) {
        formData.append('attachments[]', file)
      }

      const response = await apiClient.post<HelpRequestApiResponse>(
        API_ENDPOINTS.HELP,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (event) => {
            if (!onUploadProgress) return
            const total = event.total && event.total > 0 ? event.total : 0
            const loaded = event.loaded ?? 0
            onUploadProgress({
              loaded,
              total,
              percent: total > 0 ? Math.min(100, Math.round((loaded / total) * 100)) : 0,
            })
          },
        },
      )
      if (!response.data?.data) {
        throw new Error(response.data?.message ?? '')
      }
      return response.data.data
    }

    const response = await apiClient.post<HelpRequestApiResponse>(API_ENDPOINTS.HELP, {
      title: payload.title,
      body: payload.body,
    })
    if (!response.data?.data) {
      throw new Error(response.data?.message ?? '')
    }
    return response.data.data
  },

  index: async (params: HelpListParams = {}): Promise<PaginatedResponse<HelpRequest>> => {
    const response = await apiClient.get<HelpRequestListApiResponse>(API_ENDPOINTS.HELP, {
      params: {
        page: params.page ?? 1,
        per_page: params.per_page ?? 15,
      },
    })
    const data = response.data?.data ?? []
    const meta = response.data?.meta
    return {
      data,
      current_page: meta?.current_page ?? params.page ?? 1,
      last_page: meta?.last_page ?? 1,
      per_page: meta?.per_page ?? params.per_page ?? 15,
      total: meta?.total ?? data.length,
    }
  },

  show: async (id: number): Promise<HelpRequest> => {
    const response = await apiClient.get<HelpRequestApiResponse>(API_ENDPOINTS.HELP_BY_ID(id))
    if (!response.data?.data) {
      throw new Error(response.data?.message ?? '')
    }
    return response.data.data
  },

  uploadAttachment: async (
    helpRequestId: number,
    file: File,
    onUploadProgress?: (progress: HelpUploadProgress) => void,
  ): Promise<HelpRequestAttachment> => {
    const formData = new FormData()
    formData.append('file', file)
    const response = await apiClient.post<HelpAttachmentApiResponse>(
      API_ENDPOINTS.HELP_ATTACHMENTS(helpRequestId),
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (event) => {
          if (!onUploadProgress) return
          const total = event.total && event.total > 0 ? event.total : 0
          const loaded = event.loaded ?? 0
          onUploadProgress({
            loaded,
            total,
            percent: total > 0 ? Math.min(100, Math.round((loaded / total) * 100)) : 0,
          })
        },
      },
    )
    if (!response.data?.data) {
      throw new Error(response.data?.message ?? '')
    }
    return response.data.data
  },
}
